import PropTypes from 'prop-types';

// Hooks
import { useEmployeeInfo } from '../../../hooks/useEmployeeInfo';

// Components
import SideNav from '../../molecules/SideNav/SideNav';
import LoaderWrapperSimple from '../../../contexts/LoaderWrapperSimple';

// Assets
import AstronLogo from '../../../assets/astron_logo_color.svg';

// Icons
import { HourglassHigh } from '@phosphor-icons/react';

// Styles
import styles from './RegistrationGuard.module.css';

/**
 * RegistrationGuard Component
 *
 * A wrapper component that protects routes by checking if the current user
 * has completed their account registration. It handles three states:
 * 1. Loading: Shows a loading spinner while checking employee info
 * 2. Unregistered: Shows a personalized message prompting user to contact support
 * 3. Registered: Renders the protected children components
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The protected content to render when user is registered
 * @param {string} [props.moduleName] - The name of the module requiring registration (e.g., "Jobs", "Inspectors", "Clients")
 * @returns {JSX.Element} The registration guard component
 */
const RegistrationGuard = ({ children, moduleName }) => {
  const { employeeId, isLoading, isError } = useEmployeeInfo();

  // Generate personalized messages based on module context
  const getModuleMessages = (module) => {
    const moduleMessages = {
      jobs: {
        title: 'Jobs Module Access Required',
        description:
          'To access the Jobs module and manage your work orders, your account needs to be fully set up in our system.',
        context: 'jobs management',
      },
      inspectors: {
        title: 'Inspectors Module Access Required',
        description:
          'To access the Inspectors module and manage inspector profiles, your account needs to be fully set up in our system.',
        context: 'inspector management',
      },
      clients: {
        title: 'Clients Module Access Required',
        description:
          'To access the Clients module and manage client relationships, your account needs to be fully set up in our system.',
        context: 'client management',
      },
      agencies: {
        title: 'Agencies Module Access Required',
        description:
          'To access the Agencies module and manage agency partnerships, your account needs to be fully set up in our system.',
        context: 'agency management',
      },
      vendors: {
        title: 'Vendors Module Access Required',
        description:
          'To access the Vendors module and manage vendor relationships, your account needs to be fully set up in our system.',
        context: 'vendor management',
      },
      reports: {
        title: 'Reports Module Access Required',
        description:
          'To access the Reports module and view analytics, your account needs to be fully set up in our system.',
        context: 'reporting and analytics',
      },
    };

    // Fallback for unknown modules or when moduleName is not provided
    if (!module || !moduleMessages[module.toLowerCase()]) {
      return {
        title: 'Module Access Required',
        description:
          'To access this module, your account needs to be fully set up in our system.',
        context: 'this module',
      };
    }

    return moduleMessages[module.toLowerCase()];
  };

  const messages = getModuleMessages(moduleName);

  // Show loading state while fetching employee information or when we don't have employee data yet
  if (isLoading || (!employeeId && !isError)) {
    return (
      <div className={styles.container}>
        <div className={styles.navColumn}>
          <SideNav />
        </div>
        <div className={styles['registration-guard__content']}>
          <LoaderWrapperSimple
            isLoading={true}
            data={null}
            noDataMessage=""
            errorMessage=""
          />
        </div>
      </div>
    );
  }

  // Show registration required message only when loading is complete and user is not registered
  if (!isLoading && !employeeId && isError) {
    return (
      <div className={styles.container}>
        <div className={styles.navColumn}>
          <SideNav />
        </div>
        <div className={styles['registration-guard__content']}>
          <div className="max-w-2xl mx-auto text-center bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header with gradient background */}
            <div className="bg-gradient-to-r from-brandSecondary to-brandPrimary px-8 py-12">
              <div className="flex justify-center mb-6">
                <img
                  src={AstronLogo}
                  alt="Astron Logo"
                  className="h-16 w-auto filter brightness-0 invert"
                />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Welcome to Astron Eye
              </h1>
              <p className="text-white/90 text-lg">
                {moduleName
                  ? `${messages.title}`
                  : 'Your account setup is in progress'}
              </p>
            </div>

            {/* Content section */}
            <div className="px-8 py-10">
              <div className="mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-brandPrimary/10 rounded-full mb-6">
                  <HourglassHigh
                    size={32}
                    className={`text-brandPrimary ${styles['hourglass-animated']}`}
                  />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  {moduleName ? messages.title : 'Account Setup Required'}
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed max-w-md mx-auto">
                  {messages.description} Please contact our platform support
                  team to complete your onboarding process for{' '}
                  {messages.context}.
                </p>
              </div>

              {/* Support contact card */}
              {/* <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-center space-x-3 mb-3">
                  <div className="w-2 h-2 bg-brandPrimary rounded-full"></div>
                  <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                    Support Team
                  </span>
                  <div className="w-2 h-2 bg-brandPrimary rounded-full"></div>
                </div>
                <p className="text-gray-700 font-medium">
                  Our team is ready to help you get started
                </p>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User is registered, render the protected content
  return <>{children}</>;
};

RegistrationGuard.propTypes = {
  /** The protected content to render when user is registered */
  children: PropTypes.node.isRequired,
  /** The name of the module requiring registration (e.g., "Jobs", "Inspectors", "Clients") */
  moduleName: PropTypes.string,
};

export default RegistrationGuard;
